/**
 * Supabase project configuration, derived rather than declared.
 *
 * The project is identified by `SUPABASE_PROJECT_ID` and `SUPABASE_PROJECT_REGION`
 * rather than a URL, because those are the two values the Supabase dashboard
 * actually shows and the ones an operator can check against a project without
 * copying a string. Everything else -- the API URL, the token issuer, the JWKS
 * endpoint -- is derived from the id, so a stage can only ever be internally
 * consistent.
 *
 * **Non-credential values only.** This module is reachable from the browser
 * bundle (`@dashboard-config` is imported by dashboard components), so it holds
 * only the project identity: the ref, the region, and the endpoints derived
 * from them.
 *
 * NEITHER KEY LIVES HERE. The secret key is obvious. The publishable key is
 * less so -- it is public by design in a normal Supabase app, shipped straight
 * to the browser -- but this dashboard never gives the browser a Supabase
 * session, so it has no reason to hold the key either. It is what makes
 * `/auth/v1/otp` reachable on our project, so keeping it off the client costs
 * nothing and removes a way to probe our auth endpoints. Both keys are read
 * server-side only, and the absence of any `VITE_` twin is what enforces it.
 *
 * Every stage supplies its own values through its own GitHub Environment, the
 * way `AUTH0_*` does today. A stage with none configured returns `null` here and
 * every caller no-ops -- which is what keeps local development and CI working
 * with no Supabase project at all.
 */

function envValue(key: string) {
  if (typeof process !== "undefined" && process.env?.[key]) return process.env[key]
  return import.meta.env?.[key]
}

export type SupabasePublicConfig = {
  projectId: string
  region: string
  /** `https://<project-id>.supabase.co` */
  url: string
  /** The `iss` claim on every token this project mints. Pin verification to it. */
  issuer: string
  /** Asymmetric signing keys. HS256 project secrets are never accepted. */
  jwksUrl: string
}

/**
 * A Supabase project ref is exactly 20 lowercase alphanumeric characters.
 *
 * Validating the SHAPE, not just presence, is what makes an unconfigured stage
 * fail closed. A GitHub Environment holding a placeholder like `REPLACE_ME` is
 * a non-empty string, so a presence check reports the stage CONFIGURED and
 * derives `https://REPLACE_ME.supabase.co` -- a project that does not exist,
 * announced as healthy. That is the "stage quietly pointing at the wrong
 * project" failure in its worst form, because nothing errors until a token
 * fails to verify.
 *
 * It also catches the ordinary version of the same mistake: a truncated ref, a
 * pasted URL, or a project id with the region appended.
 */
const PROJECT_REF = /^[a-z0-9]{20}$/

export function isSupabaseProjectRef(value: string) {
  return PROJECT_REF.test(value)
}

export function supabaseProjectUrl(projectId: string) {
  return `https://${projectId}.supabase.co`
}

/**
 * GoTrue namespaces its claims under `/auth/v1`, so the issuer is not the
 * project URL. Getting this wrong makes every token fail to verify with an
 * error that reads like a key problem.
 */
export function supabaseAuthIssuer(projectUrl: string) {
  return `${projectUrl}/auth/v1`
}

export function supabaseJwksUrl(projectUrl: string) {
  return `${projectUrl}/auth/v1/.well-known/jwks.json`
}

/**
 * The stage's Supabase project, or `null` when the stage has none.
 *
 * Returns `null` rather than throwing: an unconfigured stage is a supported
 * state, not an error. Callers that need a project should check
 * `isSupabaseConfigured()` and skip their work, so a dashboard with no Supabase
 * project behaves exactly as it does today.
 */
export function readSupabasePublicConfig(): SupabasePublicConfig | null {
  const projectId = envValue("SUPABASE_PROJECT_ID")?.trim()
  const region = envValue("SUPABASE_PROJECT_REGION")?.trim()
  if (!projectId || !region) return null
  // A placeholder or malformed ref means "not configured", never "configured
  // with nonsense" -- see PROJECT_REF above.
  if (!PROJECT_REF.test(projectId)) return null

  const url = supabaseProjectUrl(projectId)
  return {
    projectId,
    region,
    url,
    issuer: supabaseAuthIssuer(url),
    jwksUrl: supabaseJwksUrl(url),
  }
}

export function isSupabaseConfigured() {
  return readSupabasePublicConfig() !== null
}
